import type { ColumnDef } from "@tanstack/react-table";
import { buildAvatarDetailsColumn, buildCopyTextColumn, buildActionColumn, buildTagColumn } from "@/components/Table";
import { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { getOrderStatusTagExtras, getOrderStatusTagLabel, getOrderStatusTagType } from "@/pages/events/_utils";
import type { RegistrantRow } from "../../../types";

interface ColumnsOptions {
  isCompactLayout: boolean;
  hasAdditionalGuests: boolean;
  hasOrderColumns: boolean;
  setMenu: (opts: { rowId: string; actions: Action[] }) => void;
  onViewPerson: (pgaId: string) => void;
  onViewRegistration: (registrationId: string) => void;
}

export function getColumns({
  isCompactLayout,
  hasAdditionalGuests,
  hasOrderColumns,
  setMenu,
  onViewPerson,
  onViewRegistration
}: ColumnsOptions): ColumnDef<RegistrantRow, unknown>[] {
  return [
    ...buildAvatarDetailsColumn<RegistrantRow>({
      isCompactLayout,
      getTitle: (row) => row.registrantName,
      getSubtitle: (row) => row.programType,
      subtitleLabel: "Program",
      getPicture: (row) => row.profilePhoto,
      header: "Name",
      size: 500,
      minSize: 200,
      id: "name"
    }),
    buildCopyTextColumn<RegistrantRow>({
      accessorKey: "pgaId",
      header: "PGA ID",
      getText: (row) => row.pgaId,
      copyFieldLabel: "PGA ID",
      enableSorting: false
    }),
    ...(hasAdditionalGuests
      ? [
          {
            accessorKey: "additionalGuests",
            header: "Guests",
            enableSorting: false
          } as ColumnDef<RegistrantRow, unknown>
        ]
      : []),
    {
      accessorKey: "registeredAt",
      header: "Reg. Date",
      enableSorting: false,
      meta: { headerClassName: "show-header-in-column" }
    },
    {
      accessorKey: "canceledAt",
      header: "Canceled",
      enableSorting: false,
      meta: { headerClassName: "show-header-in-column" }
    },
    {
      accessorKey: "sessionCount",
      header: "Sessions",
      enableSorting: false,
      meta: { headerClassName: "show-header-in-column" }
    },
    ...(hasOrderColumns
      ? [
          {
            accessorKey: "amountRefunded",
            header: "Amount Refunded",
            enableSorting: false,
            meta: { headerClassName: "show-header-in-column" }
          } as ColumnDef<RegistrantRow, unknown>,
          buildTagColumn<RegistrantRow>({
            accessorKey: "orderStatus",
            header: "Order Status",
            tagSize: "medium",
            getTagLabel: (row) => getOrderStatusTagLabel(row.orderStatus),
            getTagType: (row) => getOrderStatusTagType(row.orderStatus),
            getTagExtras: (row) =>
              getOrderStatusTagExtras({
                orderStatus: row.orderStatus,
                amountRefunded: row.amountRefunded,
                isCanceled: !!row.canceledAt,
                paymentStatus: row.paymentStatus
              })
          })
        ]
      : []),
    buildActionColumn<RegistrantRow>({
      getActions: (row) => [
        {
          text: "View Registration",
          section: "Manage",
          onClick: () => onViewRegistration(row.id)
        }
      ],
      onViewClick: (row) => onViewPerson(row.pgaId),
      viewButtonTitle: "View person details",
      actionId,
      setMenu
    })
  ];
}
